import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { 
  ChevronLeft, Globe, ExternalLink, Upload, CheckCircle2, 
  Briefcase, Share2, Smartphone, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const JobBoardsPage = () => {
  const navigate = useNavigate();
  const [jobBoards, setJobBoards] = useState([]);
  const [cvs, setCvs] = useState([]);
  const [selectedCvId, setSelectedCvId] = useState('');
  const [loading, setLoading] = useState(true);
  const [shareResult, setShareResult] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [boardsRes, cvsRes] = await Promise.all([
        api.get('/job-boards'),
        api.get('/cv/list')
      ]);
      setJobBoards(boardsRes.data.job_boards || []);
      setCvs(cvsRes.data.cvs || []);
      if (cvsRes.data.cvs?.length > 0) {
        setSelectedCvId(cvsRes.data.cvs[0].id);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async (boardId) => {
    if (!selectedCvId) {
      toast.error('Please select a CV first');
      return;
    }
    try {
      const response = await api.post('/job-boards/share', { 
        cv_id: selectedCvId, 
        board_id: boardId 
      });
      setShareResult(response.data);
      toast.success(`Ready to upload to ${response.data.board_name}!`);
    } catch (error) {
      toast.error('Failed to prepare CV for sharing');
    }
  };

  const getRecommendedBadge = (boardId) => {
    if (['careers24', 'pnet', 'linkedin'].includes(boardId)) {
      return <Badge className="bg-emerald-100 text-emerald-700 text-xs">Recommended</Badge>;
    }
    if (boardId === 'jobjack') {
      return <Badge className="bg-blue-100 text-blue-700 text-xs">Youth Favourite</Badge>;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 lg:px-8 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              <ChevronLeft className="w-5 h-5 mr-1" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Globe className="w-6 h-6 text-blue-600" />
                SA Job Board Hub
              </h1>
              <p className="text-sm text-slate-500">Upload your CV to multiple job boards</p>
            </div>
          </div>
          <Badge className="bg-blue-100 text-blue-700">{jobBoards.length} Platforms</Badge>
        </div>
      </header>

      {/* CV Selection */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-slate-500" />
              <span className="font-medium text-slate-700">Select CV to share:</span>
            </div>
            <Select value={selectedCvId} onValueChange={setSelectedCvId}>
              <SelectTrigger className="w-[280px]">
                <SelectValue placeholder="Select a CV" />
              </SelectTrigger>
              <SelectContent>
                {cvs.map((cv) => (
                  <SelectItem key={cv.id} value={cv.id}>
                    {cv.cv_data?.personal_details?.full_name || 'Unnamed CV'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Tips Banner */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200">
        <div className="max-w-5xl mx-auto px-4 lg:px-8 py-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-blue-800 font-medium">Pro Tip: Register on Multiple Platforms</p>
              <p className="text-blue-700 text-sm">Different employers use different job boards. Create profiles on at least 3 platforms to maximize your visibility to recruiters.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-slate-600">Loading job boards...</p>
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 gap-4">
              {jobBoards.map((board) => (
                <Card key={board.id} className="hover:shadow-lg transition-shadow border-slate-200" data-testid={`job-board-${board.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden">
                          <img 
                            src={board.logo} 
                            alt={board.name} 
                            className="w-6 h-6"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                        <div>
                          <CardTitle className="text-lg flex items-center gap-2">
                            {board.name}
                            {getRecommendedBadge(board.id)}
                          </CardTitle>
                        </div>
                      </div>
                    </div>
                    <CardDescription className="mt-2">{board.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-slate-500 font-medium mb-2">Application Tips:</p>
                        <ul className="space-y-1">
                          {board.apply_tips.slice(0, 3).map((tip, idx) => (
                            <li key={idx} className="text-sm text-slate-600 flex items-start gap-2">
                              <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                              {tip}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="flex gap-2">
                        <a
                          href={board.register_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                          Register Free
                        </a>
                        <a
                          href={board.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Browse Jobs
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Share Result Modal */}
            {shareResult && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShareResult(null)}>
                <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
                  <div className="text-center">
                    <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Ready to Upload!</h3>
                    <p className="text-slate-600 mb-4">{shareResult.message}</p>
                    <div className="bg-slate-50 rounded-lg p-4 text-left mb-4">
                      <p className="text-xs text-slate-500 font-medium mb-2">Tips for {shareResult.board_name}:</p>
                      <ul className="space-y-1">
                        {shareResult.apply_tips?.map((tip, idx) => (
                          <li key={idx} className="text-sm text-slate-600 flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="flex gap-3">
                      <Button variant="outline" onClick={() => setShareResult(null)} className="flex-1">
                        Close
                      </Button>
                      <a
                        href={shareResult.upload_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                      >
                        Go to {shareResult.board_name}
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Stats */}
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
                <CardContent className="pt-6 text-center">
                  <p className="text-3xl font-bold text-emerald-700">100K+</p>
                  <p className="text-emerald-600 text-sm">Jobs on Careers24</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                <CardContent className="pt-6 text-center">
                  <p className="text-3xl font-bold text-blue-700">50K+</p>
                  <p className="text-blue-600 text-sm">Jobs on PNet</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
                <CardContent className="pt-6 text-center">
                  <p className="text-3xl font-bold text-indigo-700">10M+</p>
                  <p className="text-indigo-600 text-sm">LinkedIn SA Users</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                <CardContent className="pt-6 text-center">
                  <p className="text-3xl font-bold text-amber-700">2M+</p>
                  <p className="text-amber-600 text-sm">JobJack Users</p>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default JobBoardsPage;
